import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { KEY_PRIORITY_MAX, KEY_PRIORITY_MIN, parseKeyPriority } from '../lib/key-priority';
import { Field } from './Field';
import { Button, buttonClass } from './ui/Button';

export type KeyPriorityProps = {
  /** Scopes the input's id — several cards share one page. */
  readonly keyId: string;
  readonly priority: number;
  /** A save in flight for this key: the Edit control waits for it. */
  readonly busy?: boolean | undefined;
  /** Renders the editor iff passed — the caller decides who may re-rank. */
  readonly onSave?: ((priority: number) => void) | undefined;
};

const RANGE = { min: KEY_PRIORITY_MIN, max: KEY_PRIORITY_MAX };

/**
 * A key card's try-order line (plan 102 slice 05b): the value, what it means,
 * and an inline editor. A value the route would refuse never leaves the form
 * (`parseKeyPriority`); an unchanged one closes without a write.
 */
export function KeyPriority(props: KeyPriorityProps): JSX.Element {
  const [editing, setEditing] = createSignal(false);
  const [draft, setDraft] = createSignal('');
  const [invalid, setInvalid] = createSignal(false);
  const id = (): string => `key-priority-${props.keyId}`;

  const open = (): void => {
    setDraft(String(props.priority));
    setInvalid(false);
    setEditing(true);
  };

  const submit = (): void => {
    const next = parseKeyPriority(draft());
    if (next === null) {
      setInvalid(true);
      return;
    }
    setEditing(false);
    if (next !== props.priority) props.onSave?.(next);
  };

  return (
    <Show
      when={editing() && props.onSave}
      fallback={
        <span class="key-card__priority">
          <span class="key-card__priority-value">
            {t('account_keys.priority_value', { value: props.priority })}
          </span>
          <span class="key-card__priority-hint">{t('account_keys.priority_hint')}</span>
          <Show when={props.onSave}>
            <Button variant="link" size="sm" disabled={props.busy === true} onClick={open}>
              {t('account_keys.priority_edit')}
            </Button>
          </Show>
        </span>
      }
    >
      <form
        class="key-card__priority-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field
          id={id()}
          label={t('account_keys.priority_label')}
          desc={t('account_keys.priority_range', RANGE)}
          error={invalid() ? t('account_keys.priority_invalid', RANGE) : null}
        >
          {(a11y) => (
            <input
              {...a11y}
              class="input"
              type="number"
              inputMode="numeric"
              min={KEY_PRIORITY_MIN}
              max={KEY_PRIORITY_MAX}
              step="1"
              value={draft()}
              onInput={(e) => {
                setDraft(e.currentTarget.value);
                setInvalid(false);
              }}
            />
          )}
        </Field>
        <div class="key-card__priority-actions">
          <button type="submit" class={buttonClass({ size: 'sm' })}>
            {t('account_keys.priority_save')}
          </button>
          <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
            {t('account_keys.cancel')}
          </Button>
        </div>
      </form>
    </Show>
  );
}
